import { Injectable, Inject } from '@angular/core';
import { HttpClient, HttpParams, HttpErrorResponse } from '@angular/common/http';
import Env from '@ridango/core/env';
import { AuthService } from '@ridango/core';
import { Observable, throwError, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Capacitor } from '@capacitor/core';

export interface CountInfo {
    total: number;
    unread: number;
}

export interface Message {
    id: string;
    subject: string;
    redirectUrl: string | null;
    body: string;
    sendAt: string;
    channel: string;
    isRead: boolean;
}

export interface MessagesMeta {
    pageNumber: number;
    size: number;
    totalElements: number;
    totalPages: number;
}

export interface MessagesListResponse {
    data: Message[];
    meta: MessagesMeta;
}

export enum MessageStatus {
    READ = 'READ',
    UNREAD = 'UNREAD'
}

@Injectable({ providedIn: 'root' })
export class MessagesService {
    readonly DEFAULT_PAGE_SIZE = 20;
    pageSize = this.DEFAULT_PAGE_SIZE;
    page = 0;

    constructor(
        private http: HttpClient,
        @Inject('ENV') public env: Env,
        private authService: AuthService
    ) {}

    fetchMessagesList(page: number, size: number): Observable<MessagesListResponse> {
        const url = `${this.env.WMB.WMB_PUBLIC_API}/v1/notification-manager/messages?page=${page}&size=${size}`;
        return this.http.get<MessagesListResponse>(url);
    }

    fetchMessagesById(messageId: string) {
        const url = `${this.env.WMB.WMB_PUBLIC_API}/v1/notification-manager/messages/search`;
        const params = new HttpParams().set('bulkOrTemplatedMessageId', messageId);
        return this.http.get(url, { params }).pipe(
            catchError((error) => {
                console.error('Error fetching searched message:', error);
                return throwError(() => error);
            })
        );
    }

    markMessageAsReadUnread(id: string, status: MessageStatus) {
        const url = `${this.env.WMB.WMB_PUBLIC_API}/v1/notification-manager/messages`;
        return this.http.put(url, { status: status, messageIds: [id] });
    }

    getReceivedMessagesCount(): Observable<CountInfo> {
        const url = `${this.env.WMB.WMB_PUBLIC_API}/v1/notification-manager/messages/count`;

        const token = this.authService.getLocalAccessToken();
        const isDeviceCustomer = this.env.DEVICE_CUSTOMER_ENABLED && Capacitor.isNativePlatform();

        console.log('token:', token, 'isDeviceCustomer:', isDeviceCustomer);

        if (!token && !isDeviceCustomer) {
            return of({ total: 0, unread: 0 });
        }

        return this.http.get<CountInfo>(url).pipe(
            catchError((error: HttpErrorResponse) => {
                if (error.status === 401) {
                    return of({ total: 0, unread: 0 });
                }
                return throwError(() => error);
            })
        );
    }

    getPreviousPageSize(): number {
        return this.page * this.pageSize;
    }
}
